// Utils
import moment from 'moment';
import KEYWORDS from './keywords';
import MODULE_KEYWORDS from 'components/Modules/keywords';
import { GlobalUtils } from 'utils';
class CoreUtils {
	static getRequiredDatasourceParamKeys(params = {}) {
		return Object.keys(params).reduce((acc, paramKey) => {
			const paramConfig = params[paramKey];
			if (!paramConfig?.required) return acc;

			acc.add(paramKey);
			if (paramConfig.rangeWith) acc.add(paramConfig.rangeWith);
			return acc;
		}, new Set());
	}

	/**
	 * @param {Object} prevDataFilterUserParams
	 * @param {Object} dataFilterUserParams
	 * @param {Object[]} customCompareParams
	 * @returns boolean
	 */
	static checkUserParamsDiff({
		prevDataFilterUserParams = {},
		dataFilterUserParams,
		customCompareParams,
	}) {
		if (customCompareParams) {
			return !!customCompareParams.find(
				param =>
					GlobalUtils.flattenArray(dataFilterUserParams[param]) !==
					GlobalUtils.flattenArray(prevDataFilterUserParams[param]),
			);
		}
		const isDiff = {
			new: !!Object.keys(dataFilterUserParams).find(
				param =>
					GlobalUtils.flattenArray(dataFilterUserParams[param]) !==
					GlobalUtils.flattenArray(prevDataFilterUserParams[param]),
			),
			prev: !!Object.keys(prevDataFilterUserParams).find(
				param =>
					GlobalUtils.flattenArray(dataFilterUserParams[param]) !==
					GlobalUtils.flattenArray(prevDataFilterUserParams[param]),
			),
		};
		return isDiff.new || isDiff.prev;
	}

	/**
	 * @param {Object[]} subDataFilters
	 * @param {Object} dataFilterUserParams
	 * @returns boolean
	 */
	static checkRequiredUserParams({ subDataFilters, dataFilterUserParams }) {
		if (!subDataFilters || !Array.isArray(subDataFilters)) return false;
		if (subDataFilters.length === 0) return true;

		const isPendingRequired = !!subDataFilters.find(
			({ param, required, state }) =>
				required &&
				(state !== MODULE_KEYWORDS.STATE.SUCCESS ||
					!dataFilterUserParams[param] ||
					(Array.isArray(dataFilterUserParams[param]) &&
						dataFilterUserParams[param].length === 0)),
		);

		return !isPendingRequired;
	}

	static getDatasourceRangeLimit({
		params = {},
		fromParam = 'fromDateParam',
		toParam = 'toDateParam',
	}) {
		const fromMaxRangeDays = params[fromParam]?.maxRangeDays;
		const rawMaxRangeDays =
			fromMaxRangeDays !== undefined
				? fromMaxRangeDays
				: params[toParam]?.maxRangeDays;
		if (rawMaxRangeDays === undefined) return {};

		const maxRangeDays = Number(rawMaxRangeDays);
		return {
			maxRangeDays,
			valid: Number.isFinite(maxRangeDays) && maxRangeDays > 0,
			isDateRange:
				params[fromParam]?.type === 'date' && params[toParam]?.type === 'date',
		};
	}

	static isDatasourceRangeLimitExceeded({
		params = {},
		fromParam = 'fromDateParam',
		toParam = 'toDateParam',
		fromValue,
		toValue,
	}) {
		const rangeLimit = this.getDatasourceRangeLimit({
			params,
			fromParam,
			toParam,
		});
		if (rangeLimit.maxRangeDays === undefined || !rangeLimit.valid)
			return false;

		const fromDate = moment(fromValue);
		const toDate = moment(toValue);
		if (!fromDate.isValid() || !toDate.isValid()) return false;

		if (rangeLimit.isDateRange) {
			const inclusiveCalendarDays =
				Math.abs(
					toDate
						.clone()
						.startOf('day')
						.diff(fromDate.clone().startOf('day'), 'days'),
				) + 1;
			return inclusiveCalendarDays > rangeLimit.maxRangeDays;
		}

		return (
			Math.abs(toDate.valueOf() - fromDate.valueOf()) >
			rangeLimit.maxRangeDays * 24 * 60 * 60 * 1000
		);
	}

	/**
	 * Validate datasource access params before executing their SQL.
	 * Date limits count inclusive calendar days; datetime limits use exact periods.
	 * @param {Object} params
	 * @param {Object} dataFilterUserParams
	 * @returns {{ valid: boolean, reason?: string, maxRangeDays?: number }}
	 */
	static validateDatasourceParams({ params = {}, dataFilterUserParams = {} }) {
		if (!params || typeof params !== 'object') return { valid: true };

		const hasValue = value =>
			value !== undefined &&
			value !== null &&
			value !== '' &&
			(!Array.isArray(value) || value.length > 0);

		const ranges = Object.keys(params).reduce((acc, paramKey) => {
			const rangeWith = params[paramKey]?.rangeWith;
			if (!rangeWith || !params[rangeWith]) return acc;

			const isReverseCanonicalRange =
				paramKey === 'toDateParam' && rangeWith === 'fromDateParam';
			acc.push(
				isReverseCanonicalRange
					? ['fromDateParam', 'toDateParam']
					: [paramKey, rangeWith],
			);
			return acc;
		}, []);

		// Keep validating the legacy from/to pair when rangeWith is not configured.
		if (
			params.fromDateParam &&
			params.toDateParam &&
			!ranges.some(
				([fromParam, toParam]) =>
					fromParam === 'fromDateParam' && toParam === 'toDateParam',
			)
		) {
			ranges.push(['fromDateParam', 'toDateParam']);
		}

		const incompleteRange = ranges.find(
			([fromParam, toParam]) =>
				hasValue(dataFilterUserParams[fromParam]) !==
				hasValue(dataFilterUserParams[toParam]),
		);
		if (incompleteRange) return { valid: false, reason: 'incompleteRange' };

		const missingRequiredParam = Array.from(
			this.getRequiredDatasourceParamKeys(params),
		).find(paramKey => !hasValue(dataFilterUserParams[paramKey]));
		if (missingRequiredParam)
			return { valid: false, reason: 'required', param: missingRequiredParam };

		for (const [fromParam, toParam] of ranges) {
			const fromValue = dataFilterUserParams[fromParam];
			const toValue = dataFilterUserParams[toParam];
			const rangeLimit = this.getDatasourceRangeLimit({
				params,
				fromParam,
				toParam,
			});
			if (rangeLimit.maxRangeDays !== undefined && !rangeLimit.valid)
				return { valid: false, reason: 'invalidMaxRangeDays' };
			if (!hasValue(fromValue) || !hasValue(toValue)) continue;

			const fromDate = moment(fromValue);
			const toDate = moment(toValue);
			if (!fromDate.isValid() || !toDate.isValid())
				return { valid: false, reason: 'invalidDate' };

			const rangeMilliseconds = toDate.valueOf() - fromDate.valueOf();
			if (rangeMilliseconds < 0)
				return { valid: false, reason: 'invalidOrder' };

			if (rangeLimit.maxRangeDays === undefined) continue;

			if (
				this.isDatasourceRangeLimitExceeded({
					params,
					fromParam,
					toParam,
					fromValue,
					toValue,
				})
			)
				return {
					valid: false,
					reason: 'maxRangeDays',
					maxRangeDays: rangeLimit.maxRangeDays,
				};
		}

		return { valid: true };
	}

	/**
	 * @param {int} selectedProjectId
	 * @param {int} selectedOrderTargetFilterId
	 * @param {Object[]} selectedServiceIds
	 * @param {string} joinedServiceIds
	 * @param {string} prevJoinedServiceIds
	 * @param {boolean} dataFilterIsFetching
	 * @param {int} selectedDataFilterId
	 * @param {int} prevSelectedDataFilterId
	 * @param {Object} prevDataFilterUserParams
	 * @param {Object} dataFilterUserParams
	 * @param {Object[]} customCompareParams
	 * @returns boolean
	 */
	static checkMustFetchData({
		isReloadDatasource,
		// User params
		dataFilterUserParams,
		subDataFilters,
		datasourceParams,
	}) {
		return (
			isReloadDatasource &&
			this.checkRequiredUserParams({ subDataFilters, dataFilterUserParams }) &&
			this.validateDatasourceParams({
				params: datasourceParams,
				dataFilterUserParams,
			}).valid
		);
	}

	/**
	 * @param {string} dataFilterLastRowId - Last row id
	 * @param {function} mutate1ObjectInCore - Mutate direct props in feed reducer
	 * @param {function} setDatasource - Add to array datasource in module state redux
	 * @param {function} resetDatasource
	 * @returns actions object
	 */
	static onGettedDataEvents({
		dataFilterLastRowId,
		mutate1ObjectInCore,
		setDatasource,
		resetDatasource,
	}) {
		return {
			onLoading: function* () {
				if (dataFilterLastRowId === 0) resetDatasource();
				mutate1ObjectInCore('datasource', {
					status: KEYWORDS.STATUS.LOADING,
				});
			},
			onSuccess: function* ({ payload = {} }) {
				mutate1ObjectInCore('datasource', {
					status: KEYWORDS.STATUS.SUCCESS,
				});
				setDatasource({
					paging: payload.paging,
					fields: payload.fields,
					newData: payload.data,
					singleData: payload.singleData,
				});
			},
			onError: function* () {
				mutate1ObjectInCore('datasource', { status: KEYWORDS.STATUS.ERROR });
			},
		};
	}

	/**
	 * @param {Object[]} currentData
	 * @param {Object[]} newData
	 * @returns Object[]
	 */
	static appendOrResetDatasource(currentData, newData) {
		return currentData.concat(GlobalUtils.checkArray(newData));
	}

	static setDatasourceSingleData(singleData) {
		return GlobalUtils.checkObject(singleData);
	}

	static setOrResetDatasourceFields(currentFields, newFields) {
		return GlobalUtils.checkArray(newFields).length
			? GlobalUtils.checkArray(newFields)
			: currentFields;
	}

	static setDatasourcePaging({ currentData, currentPaging, newData, paging }) {
		const _paging = GlobalUtils.checkObject(paging);
		// const totalRowCount = _paging.totalRowCount || currentPaging.totalRowCount;
		return {
			...currentPaging,
			..._paging,
			totalRowCount:
				currentData.length +
				_paging.totalRowCount -
				GlobalUtils.checkArray(newData).length,
		};
	}
}

export default CoreUtils;
